import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export interface LookupOption {
  id: number;
  label: string;
  description?: string | null;
  keywords?: string | null;
}

export type LookupKind =
  | "suppliers"
  | "clients"
  | "subcontractors"
  | "workers"
  | "works"
  | "articles";

const PATHS: Record<LookupKind, string> = {
  suppliers: "/api/suppliers/options",
  clients: "/api/clients/options",
  subcontractors: "/api/subcontractors/options",
  workers: "/api/workers/options",
  works: "/api/works/options",
  articles: "/api/articles/options",
};

/**
 * Quantos resultados pede uma pesquisa no servidor. Vai sempre explícito no pedido
 * para que o cliente e o servidor não dependam de dois valores por omissão iguais
 * por coincidência: o <SearchableSelect> decide "veio tudo?" comparando com este
 * número, e se ele deixasse de bater certo passava a esconder linhas em silêncio.
 */
export const LOOKUP_SEARCH_PAGE_SIZE = 50;

export interface LookupQuery {
  term?: string;
  take?: number;
  includeInactive?: boolean;
  signal?: AbortSignal;
}

interface WorkOptionResponse {
  id: number;
  code: string;
  name: string;
}

export class LookupError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function fetchOptions(
  kind: LookupKind,
  query: LookupQuery,
): Promise<LookupOption[]> {
  const params = new URLSearchParams();
  if (query.term) params.set("term", query.term);
  if (query.take) params.set("take", String(query.take));
  if (query.includeInactive) params.set("includeInactive", "true");

  const suffix = params.toString();
  const url = `${apiBaseUrl}${PATHS[kind]}${suffix ? `?${suffix}` : ""}`;

  const response = await fetch(url, {
    credentials: "include",
    signal: query.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new LookupError(response.status, text || response.statusText);
  }

  const payload = await response.json();

  if (kind === "works") {
    return (payload as WorkOptionResponse[]).map((work) => ({
      id: work.id,
      label: work.name,
      description: work.code,
      keywords: work.code,
    }));
  }

  return payload as LookupOption[];
}

interface CacheEntry {
  at: number;
  pending: Promise<LookupOption[]>;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, CacheEntry>();

function cacheKey(kind: LookupKind, query: LookupQuery): string {
  const scope = query.includeInactive ? `${kind}:all` : kind;
  return query.take ? `${scope}:take=${query.take}` : scope;
}

type LookupListener = (kind: LookupKind) => void;

const listeners = new Set<LookupListener>();

/**
 * Avisa quem já está montado de que a lista mudou. Sem isto, invalidar só limpava o
 * Map: um seletor aberto noutro separador ficava com a lista velha até desmontar,
 * e não durante os 5 minutos do TTL — indefinidamente.
 */
export function subscribeToLookup(listener: LookupListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Lista completa de uma entidade, partilhada por todos os seletores abertos na
 * sessão. Guarda-se a promessa e não o resultado, para que três componentes a
 * montar ao mesmo tempo façam um pedido e não três.
 *
 * A pesquisa com termo nunca entra nesta cache: quem escreve quer o servidor,
 * e o componente já guarda esses resultados por termo.
 */
export function getLookupOptions(
  kind: LookupKind,
  query: LookupQuery = {},
): Promise<LookupOption[]> {
  if (query.term) {
    return fetchOptions(kind, query);
  }

  const key = cacheKey(kind, query);
  const now = Date.now();
  const cached = cache.get(key);

  if (cached && now - cached.at < CACHE_TTL_MS) {
    return cached.pending;
  }

  const entry: CacheEntry = { at: now, pending: null! };
  entry.pending = fetchOptions(kind, { ...query, signal: undefined }).catch(
    (error: unknown) => {
      // Só se apaga a própria entrada: um pedido falhado a terminar tarde não pode
      // deitar fora a entrada nova que já foi buscar a lista outra vez.
      if (cache.get(key) === entry) cache.delete(key);
      throw error;
    },
  );

  cache.set(key, entry);
  return entry.pending;
}

/** Chamar depois de criar/alterar uma entidade que alimenta um seletor. */
export function invalidateLookup(kind?: LookupKind): void {
  if (!kind) {
    cache.clear();
    listeners.forEach((listener) => Object.keys(PATHS).forEach((entry) => listener(entry as LookupKind)));
    return;
  }

  Array.from(cache.keys())
    .filter((key) => key === kind || key.startsWith(`${kind}:`))
    .forEach((key) => cache.delete(key));

  listeners.forEach((listener) => listener(kind));
}

/** Pesquisa no servidor, para listas grandes de mais para virem inteiras. */
export function searchLookupOptions(
  kind: LookupKind,
  term: string,
  signal: AbortSignal,
  take: number = LOOKUP_SEARCH_PAGE_SIZE,
): Promise<LookupOption[]> {
  return fetchOptions(kind, { term, take, signal });
}
